import React from "react";
import { useTranslation } from "react-i18next";
import { ApiInternal } from "src/api/api_internal";
import { SimpleTag } from "src/api/generated/frontend_internal_v1";
import { ConfirmDialog } from "src/components/base/confirm-dialog";
import { notify } from "src/components/base/toast";

/**
 * The properties for {@link DeleteTagDialog}
 */
export type DeleteTagDialogProps = {
    /** Whether the dialog is open */
    isOpen: boolean;
    /** Callback for closing the popup */
    onClose: () => void;
    /** Callback for successful deletion */
    onDeletion: () => void;
    /** The tag */
    tag: SimpleTag | undefined;
};

/**
 * A dialog to delete a tag. Surfaces blocking-reference errors inline so
 * the user can resolve them before retrying.
 */
export default function DeleteTagDialog(props: DeleteTagDialogProps) {
    const [t] = useTranslation("dialog-delete-tag");
    const [errors, setErrors] = React.useState<Array<string>>([]);

    React.useEffect(() => {
        if (!props.isOpen) setErrors([]);
    }, [props.isOpen]);

    if (!props.tag) return null;

    return (
        <ConfirmDialog
            open={props.isOpen}
            onClose={props.onClose}
            onConfirm={async () => {
                if (!props.tag) return;
                const res = await ApiInternal.tags.delete(props.tag.uuid);
                if (res.result === "Err") {
                    const next: Array<string> = [];
                    if (res.error.devices) {
                        next.push(t("error.devices-with-tag", { count: res.error.devices }));
                    }
                    if (res.error.access_rules) {
                        next.push(t("error.access-rules-with-tag", { count: res.error.access_rules }));
                    }
                    if (res.error.preauthkeys) {
                        next.push(t("error.preauthkeys-with-tag", { count: res.error.preauthkeys }));
                    }
                    setErrors(next);
                    return;
                }
                notify.success(t("toast.tag-deleted-successfully"));
                props.onDeletion();
            }}
            title={t("heading.delete-tag", { tag: props.tag.name })}
            description={t("description.delete-tag")}
            confirmLabel={t("button.delete-tag")}
        >
            {errors.length > 0 && (
                <div className={"mt-3 flex flex-col gap-1 text-sm text-red-500 dark:text-red-400"}>
                    {errors.map((err, idx) => (
                        <span key={idx}>{err}</span>
                    ))}
                </div>
            )}
        </ConfirmDialog>
    );
}
